import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Crown, Sparkles, X } from 'lucide-react';
import { getMinPlanForFeature, FEATURE_NAMES } from '../utils/subscriptionFeatures';

// Locked feature overlay component
export function LockedOverlay({ feature, onUpgrade, onClose }) {
  const minPlan = getMinPlanForFeature(feature);
  const featureName = FEATURE_NAMES[feature] || feature;
  
  const planInfo = {
    pro: { name: 'Pro', price: '₹299/month', color: 'violet', icon: Crown },
    family: { name: 'Family', price: '₹499/month', color: 'amber', icon: Sparkles },
  };
  
  const plan = planInfo[minPlan] || planInfo.pro;
  const PlanIcon = plan.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className={`w-16 h-16 rounded-2xl bg-${plan.color}-500/10 flex items-center justify-center mx-auto mb-4`}>
          <Lock className={`w-8 h-8 text-${plan.color}-400`} />
        </div>
        
        <h3 className="text-xl font-bold text-white mb-2">
          {featureName} is Locked
        </h3>
        
        <p className="text-sm text-slate-400 mb-6">
          Upgrade to <span className={`text-${plan.color}-400 font-semibold`}>{plan.name}</span> to unlock this feature and supercharge your learning!
        </p>
        
        <div className={`bg-${plan.color}-500/10 border border-${plan.color}-500/30 rounded-xl p-4 mb-6`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <PlanIcon className={`w-5 h-5 text-${plan.color}-400`} />
            <span className={`font-bold text-${plan.color}-400`}>{plan.name} Plan</span>
          </div>
          <p className="text-2xl font-bold text-white">{plan.price}</p>
        </div>
        
        <button
          onClick={onUpgrade}
          data-testid="upgrade-btn"
          className={`w-full bg-${plan.color}-600 hover:bg-${plan.color}-700 text-white rounded-xl h-12 font-semibold transition-all`}
        >
          Upgrade Now
        </button>
        
        <p className="text-xs text-slate-500 mt-3">
          7-day money-back guarantee • Cancel anytime
        </p>
      </motion.div>
    </motion.div>
  );
}

// Locked feature badge/button wrapper
export function LockedFeatureButton({ 
  feature, 
  subscription, 
  onClick, 
  onUpgrade,
  children, 
  className = '',
  showLockIcon = true,
}) {
  const minPlan = getMinPlanForFeature(feature);
  const isLocked = !subscription || 
    (minPlan === 'pro' && subscription === 'basic') ||
    (minPlan === 'family' && subscription !== 'family');
  
  const [showModal, setShowModal] = React.useState(false);

  const handleClick = () => {
    if (isLocked) {
      setShowModal(true);
    } else {
      onClick?.();
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className={`relative ${className} ${isLocked ? 'opacity-70' : ''}`}
        data-testid={`feature-${feature}`}
      >
        {children}
        {isLocked && showLockIcon && (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}
      </button>
      
      <AnimatePresence>
        {showModal && (
          <LockedOverlay
            feature={feature}
            onUpgrade={() => { setShowModal(false); onUpgrade?.(); }}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// Simple lock badge
export function LockBadge({ plan = 'pro' }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${
      plan === 'pro' ? 'bg-violet-500/20 text-violet-400' : 'bg-amber-500/20 text-amber-400'
    }`}>
      <Lock className="w-2.5 h-2.5" />
      {plan === 'pro' ? 'Pro' : 'Family'}
    </span>
  );
}

// Subject lock badge (for basic plan with 3 subject limit)
export function SubjectLockBadge({ onUpgrade }) {
  return (
    <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
      <Lock className="w-4 h-4" />
      <span>Upgrade to Pro for all subjects</span>
      <button
        onClick={onUpgrade}
        className="ml-auto text-amber-300 hover:text-amber-200 underline"
      >
        Upgrade
      </button>
    </div>
  );
}

export default LockedOverlay;
